import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import html2canvas from 'html2canvas';

const ShareImage = () => {
  const location = useLocation();
  const [params, setParams] = useState({});
  const [loading, setLoading] = useState(true);
  const cardRef = useRef(null);
  const [imageGenerated, setImageGenerated] = useState(false);

  // Parse URL query parameters
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const mosId = queryParams.get('mos');
    const rank = queryParams.get('rank');
    const duty = queryParams.get('duty');
    const score = queryParams.get('score');
    const month = queryParams.get('month');
    
    if (mosId && rank && score) {
      setParams({
        mosId,
        rank,
        duty: duty || '',
        score,
        month: month || ''
      });
    }
    
    setLoading(false);
  }, [location]);

  // Check if user is on mobile and redirect to app store if needed
  useEffect(() => {
    // Wait for image to be ready before attempting redirect
    if (!loading && !imageGenerated) {
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      const hasApp = false; // You would implement app detection logic here
      
      if (isMobile && !hasApp) {
        // Set a timer to redirect after a short delay to allow image to display
        const timer = setTimeout(() => {
          const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
          if (isIOS) {
            // Replace with your App Store URL
            window.location.href = 'https://apps.apple.com/app/devilrank/id1234567890';
          } else {
            // Replace with your Google Play URL
            window.location.href = 'https://play.google.com/store/apps/details?id=com.devilrank.app';
          }
        }, 2000); // Delay to allow image to be seen
        
        return () => clearTimeout(timer);
      }
    }
  }, [loading, imageGenerated]);

  // Generate image from the rendered card
  useEffect(() => {
    if (!loading && cardRef.current && params.mosId) {
      setTimeout(() => {
        html2canvas(cardRef.current, {
          scale: 2, // Higher resolution
          backgroundColor: null,
          logging: false
        }).then(canvas => {
          // Replace the card with the canvas image
          const cardContainer = document.getElementById('card-container');
          if (cardContainer) {
            cardContainer.innerHTML = '';
            canvas.style.width = '100%';
            canvas.style.borderRadius = '12px';
            canvas.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
            cardContainer.appendChild(canvas);
            setImageGenerated(true);
          }
        });
      }, 100); // Small delay to ensure card is fully rendered
    }
  }, [params, loading]);

  if (loading) {
    return <div className="container text-center py-5">Loading...</div>;
  }

  if (!params.mosId || !params.rank || !params.score) {
    return (
      <div className="container text-center py-5">
        <h1>Invalid Share Link</h1>
        <p>The link you followed is missing required information.</p>
        <a href="/" className="btn btn-primary mt-3">Go to DevilRank Homepage</a>
      </div>
    );
  }

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-md-6">
          <div id="card-container">
            <div 
              ref={cardRef} 
              className="card border-0 shadow" 
              style={{
                borderRadius: '12px',
                overflow: 'hidden',
                backgroundColor: '#fff'
              }}
            >
              {/* Header */}
              <div className="card-header text-white" style={{ backgroundColor: '#0C2340', padding: '15px 20px' }}>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-0">MOS {params.mosId}</h5>
                    <small>U.S. Marine Corps</small>
                  </div>
                  <img 
                    src="/logo.png" 
                    alt="DevilRank Logo" 
                    height="40" 
                    style={{ borderRadius: '50%' }}
                  />
                </div>
              </div>
              
              {/* Body */}
              <div className="card-body p-4">
                <div className="mb-4">
                  <h2 className="card-title mb-3" style={{ color: '#0C2340' }}>
                    Score for {params.month ? params.month : 'Month'}
                  </h2>
                  <div className="d-flex align-items-baseline">
                    <span 
                      className="display-4 fw-bold me-2" 
                      style={{ color: '#A6192E' }}
                    >
                      {params.score}
                    </span>
                    <span className="text-muted">{params.score === 'Closed' ? '' : 'points'}</span>
                  </div>
                </div>
                
                <hr className="my-4" />
                
                <div className="row">
                  <div className="col-6">
                    <p className="text-muted mb-1">Rank</p>
                    <p className="fw-bold">{params.rank}</p>
                  </div>
                  {params.duty && (
                    <div className="col-6">
                      <p className="text-muted mb-1">Duty</p>
                      <p className="fw-bold">{params.duty}</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Footer */}
              <div className="card-footer bg-light py-3 text-center">
                <small className="text-muted">
                  Get real-time MOS updates with the DevilRank app
                </small>
              </div>
            </div>
          </div>
          
          <div className="text-center mt-4">
            <p>Download the DevilRank app for the latest MOS cutoff scores</p>
            <div className="d-flex justify-content-center gap-3 mt-3">
              <a href="https://apps.apple.com/app/devilrank/id1234567890" target="_blank" rel="noopener noreferrer">
                <img src="/static/img/app-store-badge.svg" alt="App Store" height="40" />
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.devilrank.app" target="_blank" rel="noopener noreferrer">
                <img src="/static/img/google-play-badge.svg" alt="Google Play" height="40" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareImage;